import { useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import {
  businessVerificationStatusLabel,
  BusinessVerificationStatus,
  type BusinessSummaryDto,
} from '@api/endpoints/businesses'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { SearchField } from '@components/common/SearchField'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import { useBusinesses } from '../hooks/useBusinesses'
import { DetailField as Detail } from './DetailField'

function statusColor(status: number): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case BusinessVerificationStatus.Verified:
      return 'success'
    case BusinessVerificationStatus.Pending:
      return 'warning'
    case BusinessVerificationStatus.Suspended:
    case BusinessVerificationStatus.Rejected:
      return 'error'
    default:
      return 'default'
  }
}

function StatusChip({ business }: { business: BusinessSummaryDto }) {
  return (
    <Chip
      size="small"
      label={businessVerificationStatusLabel[business.verificationStatus] ?? business.verificationStatus}
      color={statusColor(business.verificationStatus)}
      variant={business.isVerified ? 'filled' : 'outlined'}
    />
  )
}

const columns: DataTableColumn<BusinessSummaryDto>[] = [
  {
    key: 'name',
    header: 'Name',
    primary: true,
    render: (b) => (
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600}>
          {b.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
          /{b.slug}
          {b.tagline ? ` · ${b.tagline}` : ''}
        </Typography>
      </Box>
    ),
  },
  { key: 'status', header: 'Status', render: (b) => <StatusChip business={b} /> },
  { key: 'location', header: 'Location', hideBelow: 'md', render: (b) => b.primaryLocationSummary || '—' },
  { key: 'locations', header: 'Locations', align: 'right', hideBelow: 'md', render: (b) => b.locationCount },
  { key: 'created', header: 'Created', hideBelow: 'md', render: (b) => new Date(b.createdAt).toLocaleDateString() },
]

function BusinessDetailDialog({ business, onClose }: { business: BusinessSummaryDto | null; onClose: () => void }) {
  const dialogProps = useMobileDialogProps()
  return (
    <Dialog open={business != null} onClose={onClose} maxWidth="sm" fullWidth {...dialogProps}>
      {business && (
        <>
          <DialogTitle sx={{ overflowWrap: 'anywhere' }}>{business.name}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.5}>
              <Box>
                <StatusChip business={business} />
              </Box>
              <Detail label="Slug" value={`/${business.slug}`} />
              {business.tagline && <Detail label="Tagline" value={business.tagline} />}
              <Detail label="Description" value={business.description || '—'} />
              <Detail label="Primary location" value={business.primaryLocationSummary || '—'} />
              <Detail label="Locations" value={String(business.locationCount)} />
              <Detail label="Created" value={new Date(business.createdAt).toLocaleString()} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

export function BusinessesTab() {
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [selected, setSelected] = useState<BusinessSummaryDto | null>(null)

  const { data, isLoading, isError, error, refetch } = useBusinesses({
    query: query || undefined,
    page: page + 1,
    pageSize,
  })

  return (
    <>
      <DataTable
        rows={data?.items ?? []}
        columns={columns}
        getRowKey={(b) => b.id}
        loading={isLoading}
        error={isError ? (error as Error)?.message || 'Failed to load businesses.' : null}
        onRetry={() => refetch()}
        emptyMessage={query ? `No businesses match "${query}".` : 'No businesses yet.'}
        page={page}
        pageSize={pageSize}
        total={data?.totalCount ?? 0}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onRowClick={(row) => setSelected(row)}
        toolbar={
          <SearchField
            value={query}
            onSearch={(q) => {
              setQuery(q)
              setPage(0)
            }}
            placeholder="Search businesses"
          />
        }
        renderCard={(b) => (
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {b.name}
              </Typography>
              <StatusChip business={b} />
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {b.primaryLocationSummary || 'No location'} · {b.locationCount} location{b.locationCount === 1 ? '' : 's'}
            </Typography>
          </Box>
        )}
      />
      <BusinessDetailDialog business={selected} onClose={() => setSelected(null)} />
    </>
  )
}
